/**
 * Bundle Builder - Skio Dynamic Box Integration
 *
 * This file handles:
 * - Fetching data from Skio API
 * - Rendering hero, subscription buttons, slider, and products
 * - Managing cart state (selectedProducts Map)
 * - Listening for bundle:cartUpdate events from product cards
 * - Updating MY BOX (desktop + mobile)
 * - Calculating subtotals with discounts
 * - Slider fill animation
 * - Mobile MY BOX toggle
 * - Checkout validation and Shopify cart integration
 *
 * State is managed in the BundleBuilderState object.
 * All UI updates flow through dedicated render functions.
 */

;(function () {
  'use strict'

  /**
   * ============================================================================
   * STATE MANAGEMENT
   * ============================================================================
   */

  const STORAGE_KEY = 'bundle_builder_state'

  /** Filled when `renderProducts` starts the background market fetch; used to defer reconciliation. */
  let lastMarketPriceFetch = null
  let lastMarketPriceHandles = null

  const BundleBuilderState = {
    boxData: null, // Full Skio API response (DynamicBox object)
    boxId: null, // Required for cart (_dynamicBoxId property)
    selectedProducts: new Map(), // variantId => {quantity, title, variantTitle, price, compareAtPrice, image}
    currentFrequency: 'ONE TIME', // Current selected frequency ("ONE TIME" or selling plan title)
    currentSellingPlanId: null, // Selected selling plan ID (null for ONE TIME)
    currentPlanVariantMapping: null, // Variant mapping for current plan {variantId: {adjustedPrice, planId}}
    percentDiscountTiers: {}, // Discount tiers from API {items: discount%}
    discountTiersList: [], // Sorted array of {items, discount} for slider positioning
    minItems: 3, // Minimum items required for bundle
    currencyCode: 'USD', // Currency code from API
    settings: {}, // Settings object from API (header, subheader, etc.)
    sellingPlanGroup: {}, // Selling plan group from API
    allProducts: [], // All products from selectableProductVariants
    skioProductsByHandle: new Map(), // productHandle => full Skio product object (for direct lookups)
    // String(variantId) => {
    //   price, priceFormatted, compareAtPrice, compareAtFormatted,
    //   allocations: { [sellingPlanId]: { price, priceFormatted, compareAtPrice, compareAtFormatted } }
    // }
    // Prices here are *market-currency cents* as rendered by Shopify's Liquid
    // `money` filter — they reflect any manual per-market pricing and do NOT
    // need further rate conversion. See fetchMarketPricesForProducts().
    marketPrices: new Map(),
  }

  /**
   * ============================================================================
   * SESSION PERSISTENCE
   * ============================================================================
   */

  /**
   * Save current bundle state to sessionStorage
   */
  function saveToSession() {
    try {
      const data = {
        selectedProducts: Array.from(BundleBuilderState.selectedProducts.entries()),
        currentFrequency: BundleBuilderState.currentFrequency,
        currentSellingPlanId: BundleBuilderState.currentSellingPlanId,
        boxId: BundleBuilderState.boxId,
      }
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data))
    } catch (e) {
      // sessionStorage not available or quota exceeded
    }
  }

  /**
   * Load bundle state from sessionStorage
   * Called after products are rendered so we can update card UIs
   */
  function loadFromSession() {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY)
      if (!stored) return false

      const data = JSON.parse(stored)

      // Only restore if same bundle (boxId matches)
      if (data.boxId !== BundleBuilderState.boxId) {
        sessionStorage.removeItem(STORAGE_KEY)
        return false
      }

      // Restore frequency/plan if different from current
      if (data.currentFrequency && data.currentFrequency !== BundleBuilderState.currentFrequency) {
        // Find and click the matching frequency button
        const freqBtns = document.querySelectorAll('.bundle-builder__freq-btn')
        freqBtns.forEach((btn) => {
          const btnText = btn.textContent || btn.innerText
          if (btnText.includes(data.currentFrequency.toUpperCase())) {
            btn.click()
          }
        })
      }

      // Restore selected products
      if (data.selectedProducts && data.selectedProducts.length > 0) {
        data.selectedProducts.forEach(([variantId, item]) => {
          BundleBuilderState.selectedProducts.set(variantId, item)

          // Update product card UI
          const card = document.querySelector(`.bundle-card[data-variant-id="${variantId}"]`)
          if (card) {
            const price = card.dataset.price
            const compareAtPrice = card.dataset.compareAtPrice || price
            if (window.BundleBuilder && window.BundleBuilder.updateBundleCardActions) {
              window.BundleBuilder.updateBundleCardActions(card, item.quantity, price, variantId, compareAtPrice)
            }
          }
        })

        // Update UI
        updateCartUI()
        updateSliderFill()

        console.log('Bundle Builder: Restored', BundleBuilderState.selectedProducts.size, 'items from session')
        return true
      }

      return false
    } catch (e) {
      // Invalid data or sessionStorage not available
      sessionStorage.removeItem(STORAGE_KEY)
      return false
    }
  }

  /**
   * Clear session storage (called on successful checkout)
   */
  function clearSession() {
    try {
      sessionStorage.removeItem(STORAGE_KEY)
    } catch (e) {
      // Ignore errors
    }
  }

  /**
   * ============================================================================
   * INITIALIZATION
   * ============================================================================
   */

  document.addEventListener('DOMContentLoaded', function () {
    initBundleBuilder()
  })

  /**
   * Initialize the bundle builder
   */
  async function initBundleBuilder() {
    // console.log('Bundle Builder: Initializing...');

    const container = document.querySelector('.bundle-builder')
    if (!container) {
      console.error('Bundle Builder: Container not found')
      return
    }

    const productPlatformId = container.dataset.productPlatformId
    if (!productPlatformId) {
      console.error('Bundle Builder: Product Platform ID not configured')
      return
    }

    try {
      // Show loading state
      showLoading()

      // Fetch bundle data from Skio API
      // console.log('Bundle Builder: Fetching data for product ID:', productPlatformId);
      const boxData = await fetchBundleData(productPlatformId)
      // console.log('Bundle Builder: API response:', boxData);

      // Store data in state
      BundleBuilderState.boxData = boxData
      BundleBuilderState.boxId = boxData.boxId
      BundleBuilderState.percentDiscountTiers = boxData.percentDiscountTier || {}
      BundleBuilderState.currencyCode = boxData.currencyCode || 'USD'
      BundleBuilderState.minItems = boxData.sizeRange?.[0] || 3

      // Build sorted discount tiers list for slider
      BundleBuilderState.discountTiersList = Object.entries(BundleBuilderState.percentDiscountTiers)
        .map(([items, discount]) => ({ items: parseInt(items), discount }))
        .sort((a, b) => a.items - b.items)
      BundleBuilderState.settings = boxData.settings || {}
      BundleBuilderState.sellingPlanGroup = boxData.sellingPlanGroup || {}
      BundleBuilderState.allProducts = boxData.selectableProductVariants || []
      BundleBuilderState.currentPlanVariantMapping = null // ONE TIME by default

      // Hide loading, show content
      hideLoading()

      // Render all UI components
      renderHero()
      renderSubscriptionButtons()
      renderSlider()
      await renderProducts()

      // Initialize event listeners
      initCartEventListeners()
      initMobileCartToggle()
      initCheckoutHandlers()

      // Restore state from session (after products are rendered)
      loadFromSession()

      // Market prices are fetched in parallel with the grid; run reconciliation
      // only after cards exist and session cart state is applied (otherwise
      // `reconcileMarketPricesUI` no-ops or overwrites a wrong restore order).
      flushMarketPriceReconciliation()

      // console.log('Bundle Builder: Initialization complete');
    } catch (error) {
      console.error('Bundle Builder: Initialization error:', error)
      showError('Unable to load bundle builder. Please try again later.')
    }
  }

  /**
   * ============================================================================
   * SKIO API INTEGRATION
   * ============================================================================
   */

  /**
   * Fetch bundle data from Skio API
   * @param {String} productPlatformId - Shopify product ID
   * @returns {Promise<Object>} DynamicBox object from API
   */
  async function fetchBundleData(productPlatformId) {
    const response = await fetch('https://api.skio.com/storefront-http/get-dynamic-box', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ productPlatformId }),
    })

    if (!response.ok) {
      throw new Error(`Skio API Error: ${response.status} ${response.statusText}`)
    }

    const data = await response.json()

    if (!data.ok || !data.DynamicBox) {
      throw new Error('Invalid Skio API response structure')
    }

    return data.DynamicBox
  }

  /**
   * ============================================================================
   * UI RENDERING FUNCTIONS
   * ============================================================================
   */

  /**
   * Render hero section (title and subtitle)
   */
  function renderHero() {
    // console.log('Bundle Builder: Rendering hero...');

    const titleEl = document.getElementById('bundle-hero-title')
    const subtitleEl = document.getElementById('bundle-hero-subtitle')

    // Calculate highest discount from tiers
    let highestDiscount = 0
    if (BundleBuilderState.discountTiersList && BundleBuilderState.discountTiersList.length > 0) {
      BundleBuilderState.discountTiersList.forEach((tier) => {
        const discount = parseFloat(tier.discount) || 0
        if (discount > highestDiscount) {
          highestDiscount = discount
        }
      })
    }

    // Set title to "SAVE UP TO X%" where X is the highest discount
    if (titleEl) {
      if (highestDiscount > 0) {
        titleEl.textContent = `SAVE UP TO ${highestDiscount}%`
      } else {
        // Fallback if no discount tiers available
        titleEl.textContent = BundleBuilderState.settings.header || ''
      }
    }

    // Subtitle unchanged - still uses Skio API subheader
    if (subtitleEl && BundleBuilderState.settings.subheader) {
      subtitleEl.textContent = BundleBuilderState.settings.subheader
    }
  }

  /**
   * Render subscription frequency buttons
   * Populates both desktop (inside media banner) and mobile (below media banner) containers
   */
  function renderSubscriptionButtons() {
    // console.log('Bundle Builder: Rendering subscription buttons...');

    const desktopContainer = document.getElementById('bundle-frequency-desktop')
    const mobileContainer = document.getElementById('bundle-frequency-mobile')

    if (!desktopContainer && !mobileContainer) return

    // Clear both containers
    if (desktopContainer) desktopContainer.innerHTML = ''
    if (mobileContainer) mobileContainer.innerHTML = ''

    // Create subscription buttons from selling plan group
    const plans = Object.entries(BundleBuilderState.sellingPlanGroup)
      .map(([planId, plan]) => ({ planId, ...plan }))
      .sort((a, b) => (a.position || 0) - (b.position || 0))

    // Create buttons for desktop container
    if (desktopContainer) {
      const oneTimeBtnDesktop = createFrequencyButton('ONE TIME', null, null, true)
      desktopContainer.appendChild(oneTimeBtnDesktop)

      plans.forEach((plan, index) => {
        const discountPercent = plan.priceAdjustmentAmount || 0
        const btn = createFrequencyButton(
          plan.title,
          discountPercent,
          plan.planId,
          false // Not active by default - ONE TIME is default
        )
        desktopContainer.appendChild(btn)
      })
    }

    // Create buttons for mobile container
    if (mobileContainer) {
      const oneTimeBtnMobile = createFrequencyButton('ONE TIME', null, null, true)
      mobileContainer.appendChild(oneTimeBtnMobile)

      plans.forEach((plan, index) => {
        const discountPercent = plan.priceAdjustmentAmount || 0
        const btn = createFrequencyButton(
          plan.title,
          discountPercent,
          plan.planId,
          false // Not active by default - ONE TIME is default
        )
        mobileContainer.appendChild(btn)
      })
    }

    // Set ONE TIME as active by default
    BundleBuilderState.currentFrequency = 'ONE TIME'
    BundleBuilderState.currentSellingPlanId = null
  }

  /**
   * Create a frequency button element
   * @param {String} title - Button title (e.g., "30 days" or "ONE TIME")
   * @param {Number|null} discount - Discount percentage (null for ONE TIME)
   * @param {String|null} planId - Selling plan ID (null for ONE TIME)
   * @param {Boolean} isActive - Whether button should be active initially
   * @returns {HTMLElement} Button element
   */
  function createFrequencyButton(title, discount, planId, isActive = false) {
    const btn = document.createElement('button')
    btn.type = 'button'
    btn.className = 'bundle-builder__freq-btn'

    if (planId) {
      // Subscription button with split layout
      btn.classList.add('bundle-builder__freq-btn--subscription')

      const titleSpan = document.createElement('span')
      titleSpan.className = 'bundle-builder__freq-btn-text'
      titleSpan.textContent = title.toUpperCase()

      const divider = document.createElement('span')
      divider.className = 'bundle-builder__freq-btn-divider'

      const discountSpan = document.createElement('span')
      discountSpan.className = 'bundle-builder__freq-btn-text'
      discountSpan.textContent = `${discount}% OFF`

      btn.appendChild(titleSpan)
      btn.appendChild(divider)
      btn.appendChild(discountSpan)
    } else {
      // ONE TIME button
      btn.textContent = title
    }

    if (isActive) {
      btn.classList.add('active')
    }

    btn.addEventListener('click', () => {
      handleFrequencyChange(title, planId, btn)
    })

    return btn
  }

  /**
   * Handle frequency button click
   * @param {String} frequency - Frequency title
   * @param {String|null} planId - Selling plan ID
   * @param {HTMLElement} clickedBtn - Clicked button element
   */
  async function handleFrequencyChange(frequency, planId, clickedBtn) {
    // console.log('Bundle Builder: Frequency changed to:', frequency);

    // Update state
    BundleBuilderState.currentFrequency = frequency
    BundleBuilderState.currentSellingPlanId = planId

    // Update variant mapping based on selected plan
    if (planId && BundleBuilderState.sellingPlanGroup[planId]) {
      BundleBuilderState.currentPlanVariantMapping = BundleBuilderState.sellingPlanGroup[planId].variantMapping || null
      const variantCount = BundleBuilderState.currentPlanVariantMapping
        ? Object.keys(BundleBuilderState.currentPlanVariantMapping).length
        : 0
      // console.log(`Bundle Builder: Updated variant mapping for plan ${planId} - ${variantCount} variants available`);
    } else {
      // ONE TIME - no variant mapping needed
      BundleBuilderState.currentPlanVariantMapping = null
      // console.log('Bundle Builder: Cleared variant mapping (ONE TIME)');
    }

    // Update button active states
    document.querySelectorAll('.bundle-builder__freq-btn').forEach((btn) => {
      btn.classList.remove('active')
    })
    clickedBtn.classList.add('active')

    // Clear ALL items from "My Box" when switching subscription options
    // Get all current variant IDs before clearing (for resetting product cards)
    const variantIdsToReset = Array.from(BundleBuilderState.selectedProducts.keys())

    // Clear the entire cart state
    BundleBuilderState.selectedProducts.clear()

    // Reset all product card quantity selectors to 0
    variantIdsToReset.forEach((variantId) => {
      const matchingCard = document.querySelector(`.bundle-card[data-variant-id="${variantId}"]`)
      if (matchingCard) {
        const qtyInput = matchingCard.querySelector('.bundle-card__qty-input')
        if (qtyInput) {
          qtyInput.value = 0
        }
      }

      // Also call legacy reset function if available
      if (window.resetBundleCard) {
        window.resetBundleCard(variantId)
      }
    })

    // console.log('Bundle Builder: Cleared all items from cart due to subscription change');

    // Re-render products to show only available products/variants for this plan
    await renderProducts()

    flushMarketPriceReconciliation()

    // Recalculate and update cart UI (discounts may have changed)
    updateCartUI()
    updateSliderFill()
  }

  /**
   * Render quantity slider with discount tier markers
   */
  function renderSlider() {
    // console.log('Bundle Builder: Rendering slider...');

    const markersContainer = document.getElementById('bundle-slider-markers')
    if (!markersContainer) return

    markersContainer.innerHTML = ''

    const tiers = BundleBuilderState.discountTiersList
    if (tiers.length === 0) return

    // Create marker elements - evenly spaced across the bar
    tiers.forEach((tier, index) => {
      const markerEl = createSliderMarker(tier.items, tier.discount, index, tiers.length)
      markersContainer.appendChild(markerEl)
    })
  }

  /**
   * Create a slider marker element
   * @param {Number} items - Number of items for this marker
   * @param {String|null} discount - Discount percentage (null if no discount)
   * @param {Number} index - Index of this marker in the list
   * @param {Number} totalMarkers - Total number of markers
   * @returns {HTMLElement} Marker element
   */
  function createSliderMarker(items, discount, index, totalMarkers) {
    const marker = document.createElement('div')
    marker.className = 'bundle-builder__slider-marker'
    marker.dataset.items = items

    // Calculate position as percentage - evenly spaced at 25%, 50%, 75%
    const positions = [25, 50, 75]
    const position = positions[index] || 50
    marker.style.left = `${position}%`

    // Create dot
    const dot = document.createElement('div')
    dot.className = 'bundle-builder__slider-dot'

    // Create top label (item count)
    const labelTop = document.createElement('div')
    labelTop.className = 'bundle-builder__slider-label-top'
    labelTop.textContent = `${items} ITEM${items !== 1 ? 'S' : ''}`

    // Create bottom label (discount percentage)
    if (discount) {
      const labelBottom = document.createElement('div')
      labelBottom.className = 'bundle-builder__slider-label-bottom'
      labelBottom.textContent = `${discount}% Off`
      marker.appendChild(labelBottom)
    }

    marker.appendChild(labelTop)
    marker.appendChild(dot)

    return marker
  }

  /**
   * Update slider fill bar based on current item count
   * Fill progresses based on position relative to discount tier markers
   */
  function updateSliderFill() {
    const totalItems = getTotalItemCount()
    const tiers = BundleBuilderState.discountTiersList
    const fillBar = document.getElementById('bundle-slider-fill')

    if (!fillBar || tiers.length === 0) return

    const positions = [25, 50, 75]
    let percentage = 0

    if (totalItems <= 0) {
      percentage = 0
    } else if (totalItems >= tiers[tiers.length - 1].items) {
      // At or beyond the last tier - fill to 100%
      percentage = 100
    } else {
      // Find position between tiers
      for (let i = 0; i < tiers.length; i++) {
        const tierItems = tiers[i].items
        const tierPosition = positions[i] || 75

        if (totalItems < tierItems) {
          // We're before this tier
          if (i === 0) {
            // Before first tier - scale from 0 to first tier position
            percentage = (totalItems / tierItems) * tierPosition
          } else {
            // Between previous tier and this tier
            const prevTier = tiers[i - 1]
            const prevPosition = positions[i - 1] || 25
            const itemsInRange = tierItems - prevTier.items
            const itemsProgress = totalItems - prevTier.items
            const positionRange = tierPosition - prevPosition
            percentage = prevPosition + (itemsProgress / itemsInRange) * positionRange
          }
          break
        }
      }
    }

    fillBar.style.width = `${percentage}%`

    // console.log(`Bundle Builder: Slider updated - ${totalItems} items (${percentage.toFixed(1)}%)`);
  }

  /**
   * Render products from Skio API data
   * Filters products based on selected selling plan
   */
  async function renderProducts() {
    // console.log('Bundle Builder: Rendering products...');

    const container = document.getElementById('bundle-products')
    if (!container) return

    // Get all products from state
    let selectableVariants = BundleBuilderState.allProducts || []

    if (selectableVariants.length === 0) {
      container.innerHTML = '<p style="font-family: var(--font-primary);">No products available for this bundle.</p>'
      return
    }

    // Filter products based on selected selling plan
    if (BundleBuilderState.currentPlanVariantMapping) {
      // Subscription selected - filter products to only include those with variants in this plan
      // Extract numeric IDs from GID keys (e.g., "gid://shopify/ProductVariant/123" -> "123")
      const allowedVariantIds = new Set(
        Object.keys(BundleBuilderState.currentPlanVariantMapping).map((gid) => extractVariantId(gid))
      )

      // console.log('Bundle Builder: Allowed variant IDs for plan:', Array.from(allowedVariantIds));

      selectableVariants = selectableVariants.filter((product) => {
        // Check if product has at least one variant in the current plan
        const hasAllowedVariant = product.productVariants.some((variant) => {
          const variantId = extractVariantId(variant.platformId)
          return allowedVariantIds.has(variantId)
        })
        return hasAllowedVariant
      })

      // console.log(`Bundle Builder: Filtered to ${selectableVariants.length} products for plan ${BundleBuilderState.currentSellingPlanId}`);
    } else {
      // console.log('Bundle Builder: ONE TIME - showing all products');
    }

    if (selectableVariants.length === 0) {
      container.innerHTML =
        '<p style="font-family: var(--font-primary);">No products available for this subscription plan.</p>'
      return
    }

    // Populate Skio product lookup map for direct access to variant data
    BundleBuilderState.skioProductsByHandle.clear() // Clear previous data
    selectableVariants.forEach((skioProduct) => {
      BundleBuilderState.skioProductsByHandle.set(skioProduct.productHandle, skioProduct)
    })
    // console.log('Bundle Builder: Stored', BundleBuilderState.skioProductsByHandle.size, 'products in lookup map');

    // Show loading state
    container.innerHTML = '<p style="font-family: var(--font-primary);">Loading products...</p>'

    // Populate market-aware prices in the background. Skio's API returns
    // base-currency prices only; `resolveVariantPrice` falls back until the
    // bundle-variant-prices map fills in. Do not block grid render on this
    // network work. Reconciliation runs from `flushMarketPriceReconciliation`
    // after the grid (and any session restore) is ready.
    const marketPriceHandles = Array.from(new Set(selectableVariants.map((p) => p.productHandle).filter(Boolean)))
    lastMarketPriceHandles = marketPriceHandles
    lastMarketPriceFetch = fetchMarketPricesForProducts(selectableVariants).catch((err) => {
      console.warn('Bundle Builder: fetchMarketPricesForProducts failed', err)
      return null
    })

    // Render all products in parallel for better performance
    const productPromises = selectableVariants.map((skioProduct) => renderProductCard(skioProduct))

    const productCards = await Promise.all(productPromises)

    // Clear container and insert all product cards
    container.innerHTML = ''

    productCards.forEach((card) => {
      if (card) {
        // Wrap card in same structure as popular-products section
        const li = document.createElement('li')
        li.className = 'popular-products__item collection-product-card'
        li.appendChild(card)
        container.appendChild(li)
      }
    })

    // Initialize product card JavaScript
    if (window.initBundleProductCards) {
      window.initBundleProductCards()
    }

    // Note: Mobile swatch scrolling is handled automatically by product-card-swatches.js

    // console.log(`Bundle Builder: Rendered ${productCards.filter(c => c).length} products`);
  }

  /**
   * Render a single product card using Section Rendering API
   *
   * This fetches the collection card HTML from Shopify with full product context,
   * then swaps the buttons with bundle-specific quantity selector.
   *
   * @param {Object} skioProduct - Product object from Skio API
   * @returns {Promise<HTMLElement|null>} Product card element
   */
  async function renderProductCard(skioProduct) {
    const productHandle = skioProduct.productHandle
    const sectionId = 'bundle-product-card-renderer'

    // console.log('Bundle Builder: Rendering product card for:', skioProduct.productTitle);

    try {
      // Fetch rendered HTML with product context using Section Rendering API
      const response = await fetch(`/products/${productHandle}?section_id=${sectionId}`)

      if (!response.ok) {
        throw new Error(`Failed to render product: ${response.status} ${response.statusText}`)
      }

      const html = await response.text()

      // Parse HTML and extract the product card
      const parser = new DOMParser()
      const doc = parser.parseFromString(html, 'text/html')
      const productCard = doc.querySelector('.card-wrapper')

      if (!productCard) {
        console.error('Product card not found in rendered HTML for:', productHandle)
        return null
      }

      // Mark as bundle card for targeting
      productCard.classList.add('bundle-card')
      productCard.setAttribute('data-bundle-card', '')

      // Get first available variant for pricing
      const firstVariant = skioProduct.productVariants[0]
      if (!firstVariant) {
        console.error('No variants available for:', productHandle)
        return null
      }

      const variantId = extractVariantId(firstVariant.platformId)

      // Pull the authoritative market-aware price for this variant (and, if
      // a subscription is active, its selling-plan allocation). Falls back
      // to Skio base cents + plan adjustment if the market fetch didn't land.
      const { price, compareAtPrice, priceFormatted } = resolveVariantPrice(firstVariant)

      // Add data attributes for cart functionality
      productCard.setAttribute('data-product-handle', productHandle)
      productCard.setAttribute('data-product-title', skioProduct.productTitle)
      productCard.setAttribute('data-variant-id', variantId)
      productCard.setAttribute('data-variant-title', firstVariant.productVariantTitle)
      productCard.setAttribute('data-price', price)
      productCard.setAttribute('data-compare-at-price', compareAtPrice)

      // Add variant image if available (Skio API uses imageSrc property)
      if (firstVariant.imageSrc) {
        productCard.setAttribute('data-variant-image', firstVariant.imageSrc)
      }

      // UPDATE PRICE with market-aware price (product-card.liquid already has price element)
      const priceElement = productCard.querySelector('.card__title.price')
      if (priceElement) {
        priceElement.textContent = priceFormatted || formatPrice(price)
      }

      // SWAP BUTTONS: Replace .mt-auto content with bundle-specific ADD button or quantity selector
      const buttonContainer = productCard.querySelector('.mt-auto')
      if (buttonContainer) {
        buttonContainer.className = 'bundle-card__actions'
        // Initially show ADD button (quantity is 0)
        updateBundleCardActions(productCard, 0, price, variantId, compareAtPrice || price, priceFormatted)
      }

      // Filter color swatches to only show Skio-allowed variants
      filterSwatchesBySkioVariants(productCard, skioProduct)

      // CRITICAL: After filtering, sync card data with the actually checked swatch
      // This ensures the card's data attributes match the selected radio button
      syncCardDataWithCheckedSwatch(productCard, skioProduct)

      return productCard
    } catch (error) {
      console.error('Error rendering product card:', error)
      return null
    }
  }

  /**
   * Update bundle card actions - show ADD button or quantity selector based on quantity
   *
   * When available, `priceFormatted` should be Shopify's Liquid-rendered
   * money string (passed through from the market-price fetch) — it matches
   * sitewide prices exactly, including manual per-market values that Intl
   * can't reproduce from raw cents.
   *
   * @param {HTMLElement} productCard - The product card DOM element
   * @param {number} quantity - Current quantity
   * @param {number} price - Product price in market-currency cents
   * @param {string} variantId - Variant ID
   * @param {number} compareAtPrice - Compare at price in market-currency cents
   * @param {string} [priceFormatted] - Optional pre-formatted market price string
   */
  function updateBundleCardActions(productCard, quantity, price, variantId, compareAtPrice, priceFormatted) {
    const buttonContainer = productCard.querySelector('.bundle-card__actions')
    if (!buttonContainer) return

    if (quantity >= 1) {
      // Show quantity selector
      buttonContainer.innerHTML = `
        <div class="bundle-card__qty-wrapper">
          <button type="button" class="bundle-card__qty-btn bundle-card__qty-decrease" aria-label="Decrease quantity">−</button>
          <input type="number" class="bundle-card__qty-input" value="${quantity}" min="0" readonly aria-label="Quantity">
          <button type="button" class="bundle-card__qty-btn bundle-card__qty-increase" aria-label="Increase quantity">+</button>
        </div>
      `
    } else {
      // Prefer the Liquid-rendered string if the caller passed one; otherwise
      // look it up from the market-prices map (populated at init time) so
      // older callers that only have cents still get sitewide-accurate
      // formatting. Falls back to `formatPrice(cents)` as a last resort.
      let displayPrice = priceFormatted
      if (!displayPrice) {
        const market = getMarketPriceData(variantId, BundleBuilderState.currentSellingPlanId)
        displayPrice = (market && market.priceFormatted) || formatPrice(price)
      }
      // Show ADD button
      buttonContainer.innerHTML = `
        <button
          type="button"
          class="bundle-card__add-btn font-primary"
          data-action="add-to-bag"
          data-variant-id="${variantId}"
          data-price="${price}"
          data-compare-at-price="${compareAtPrice}"
        >
          ADD ${displayPrice}
        </button>
      `
    }
  }

  /**
   * Format a price in the shopper's currently selected market currency.
   *
   * IMPORTANT: all cents passed in here are expected to already be in the
   * shopper's market currency — `resolveVariantPrice()` + the market-price
   * fetch handle conversion up-front by delegating to Shopify's Liquid
   * `money` filter, which respects manual per-market pricing. Multiplying
   * by `Shopify.currency.rate` a second time here would overshoot.
   *
   * Used for *computed* display values (subtotals, savings totals) where no
   * pre-formatted Liquid string exists. For single-variant prices, callers
   * should prefer the `priceFormatted` string returned by the market-price
   * fetch — it matches the rest of the site exactly.
   *
   * @param {number} cents - Price in market-currency cents
   * @returns {string} Formatted price
   */
  const ZERO_DECIMAL_CURRENCIES = (typeof window !== 'undefined' &&
    window.SACHEU_FORMAT_MONEY_ZERO_DECIMAL_CURRENCIES) || [
    'BIF',
    'CLP',
    'DJF',
    'GNF',
    'ISK',
    'JPY',
    'KMF',
    'KRW',
    'MGA',
    'PYG',
    'RWF',
    'UGX',
    'VND',
    'VUV',
    'XAF',
    'XOF',
    'XPF',
  ]

  function formatPrice(cents) {
    const marketCents = typeof cents === 'number' ? cents : parseInt(cents, 10) || 0

    const activeCurrencyCode =
      (typeof window !== 'undefined' && window.theme && window.theme.currencyCode) ||
      (typeof window !== 'undefined' && window.Shopify && window.Shopify.currency && window.Shopify.currency.active) ||
      BundleBuilderState.currencyCode ||
      'USD'

    // Prefer the market's configured symbol (cart.currency.symbol, exposed
    // via window.theme) so we match the rest of the site exactly. Without
    // this, Intl would render DKK as "DKK 250" while Shopify/Liquid renders
    // it as "kr. 250", leaving the bundle builder inconsistent with every
    // other price on the page.
    const currencySymbol =
      (typeof window !== 'undefined' && window.theme && window.theme.currencySymbol) ||
      (activeCurrencyCode === 'USD' ? '$' : activeCurrencyCode)

    const isZeroDecimal = ZERO_DECIMAL_CURRENCIES.indexOf(activeCurrencyCode) !== -1
    const amount = isZeroDecimal ? marketCents : marketCents / 100
    // Drop the decimal portion when the price is a whole currency unit so
    // "$12.00" renders as "$12" — matches the `format-money` Liquid snippet.
    const hasFractional = !isZeroDecimal && marketCents % 100 !== 0

    // Pin to 'en-US' so the decimal separator is always "." and the
    // thousands grouping is always "," regardless of the shopper's market
    // locale. Otherwise European locales would render "€19,99" style,
    // which the team does not want.
    let digits
    try {
      digits = amount.toLocaleString('en-US', {
        minimumFractionDigits: hasFractional ? 2 : 0,
        maximumFractionDigits: hasFractional ? 2 : 0,
      })
    } catch (e) {
      digits = hasFractional ? amount.toFixed(2) : String(Math.round(amount))
    }

    // Multi-character symbols (CHF, kr, kr., zł, Kč, Ft, …) are alphabetic
    // and need a space before the digits; single-glyph symbols ($, €, £, ¥)
    // stay flush against the number.
    const separator = currencySymbol.length > 1 ? ' ' : ''
    let out = currencySymbol + separator + digits
    const withCurrencyCode =
      (typeof window !== 'undefined' && window.SACHEU_FORMAT_MONEY_INCLUDE_CURRENCY_CODE) ||
      (typeof window !== 'undefined' && window.theme && window.theme.currencyCodeEnabled)
    if (withCurrencyCode && activeCurrencyCode) {
      out += ' ' + activeCurrencyCode
    }
    return out
  }

  /**
   * Filter color swatches to only show variants allowed by Skio and current selling plan
   *
   * @param {HTMLElement} productCard - The product card DOM element
   * @param {Object} skioProduct - Product object from Skio API with allowed variants
   */
  function filterSwatchesBySkioVariants(productCard, skioProduct) {
    // console.log('=== FILTERING SWATCHES FOR:', skioProduct.productTitle, '===');

    // Get base allowed variant IDs from Skio data
    let allowedVariantIds = new Set(skioProduct.productVariants.map((v) => extractVariantId(v.platformId)))

    // console.log('Skio API allowed variants:', Array.from(allowedVariantIds));
    // console.log('Total Skio variants:', allowedVariantIds.size);

    // If a subscription plan is selected, further filter by plan's variant mapping
    if (BundleBuilderState.currentPlanVariantMapping) {
      // console.log('Subscription plan selected - filtering by variant mapping');

      // Extract numeric IDs from GID keys in the variant mapping
      const planVariantIds = new Set(
        Object.keys(BundleBuilderState.currentPlanVariantMapping).map((gid) => extractVariantId(gid))
      )

      // console.log('Plan allowed variants:', Array.from(planVariantIds));
      // console.log('Total plan variants:', planVariantIds.size);

      // Intersection: only variants that are both in Skio product AND in selling plan
      allowedVariantIds = new Set([...allowedVariantIds].filter((id) => planVariantIds.has(id)))

      // console.log('Final allowed variant IDs after intersection:', Array.from(allowedVariantIds));
      // console.log('Total after filtering:', allowedVariantIds.size);
    } else {
      // console.log('ONE TIME selected - using all Skio variants');
    }

    // Find all swatch containers (both mobile and desktop structures)
    const swatchContainers = productCard.querySelectorAll('.js-color-swatches')
    // console.log('Swatch containers found:', swatchContainers.length);

    let totalRemoved = 0
    let totalKept = 0

    // Process each swatch container separately
    swatchContainers.forEach((container, containerIndex) => {
      const swatchInputs = container.querySelectorAll('input[type="radio"]')
      // console.log(`Container ${containerIndex + 1}: ${swatchInputs.length} swatch inputs found`);

      let removedInContainer = 0
      let keptInContainer = 0

      swatchInputs.forEach((input) => {
        const variantId = input.dataset.variantId
        // console.log('Checking swatch variant ID:', variantId, '- Allowed?', allowedVariantIds.has(variantId));

        // If this variant is not in the allowed list, remove it
        if (!allowedVariantIds.has(variantId)) {
          // CRITICAL FIX: Remove ONLY the input and its corresponding label
          // HTML structure: <input><label></label><input><label></label>...
          // We need to remove both the input AND its label

          const inputId = input.id
          const label = input.nextElementSibling

          // Verify the label is actually for this input
          if (label && label.tagName === 'LABEL' && label.getAttribute('for') === inputId) {
            // console.log('Removing non-allowed variant:', variantId);
            input.remove() // Remove the input
            label.remove() // Remove the label
            removedInContainer++
          } else {
            // Fallback: search for label by 'for' attribute
            const labelElement = container.querySelector(`label[for="${inputId}"]`)
            if (labelElement) {
              // console.log('Removing non-allowed variant (fallback):', variantId);
              input.remove()
              labelElement.remove()
              removedInContainer++
            } else {
              console.warn('⚠️ Could not find label for input:', inputId, '- skipping removal')
            }
          }
        } else {
          // console.log('Keeping allowed variant:', variantId);
          keptInContainer++
        }
      })

      // console.log(`Container ${containerIndex + 1}: Kept ${keptInContainer}, Removed ${removedInContainer}`);
      totalRemoved += removedInContainer
      totalKept += keptInContainer

      // After removing non-allowed swatches, ensure at least one swatch is checked
      // and tabindex follows roving pattern (first=0, rest=-1) for keyboard nav
      if (keptInContainer > 0) {
        const remainingInputs = container.querySelectorAll('input[type="radio"]')
        const hasChecked = Array.from(remainingInputs).some((input) => input.checked)

        if (!hasChecked && remainingInputs.length > 0) {
          remainingInputs[0].checked = true
        }

        remainingInputs.forEach((input, i) => {
          input.setAttribute('tabindex', i === 0 ? '0' : '-1')
        })
      } else {
        // Hide container only if we kept 0 swatches (all were removed)
        // console.log(`No swatches kept in container ${containerIndex + 1} - hiding`);
        container.style.display = 'none'
      }
    })

    // console.log(`TOTAL: Kept ${totalKept}, Removed ${totalRemoved} swatches across all containers`);
    // console.log('Final result: ' + (totalKept > 0 ? `${totalKept} variants available` : 'No variants available') + ' for: ' + skioProduct.productTitle);

    // console.log('=== END FILTERING ===\n');
  }

  /**
   * Sync product card data attributes with the actually checked swatch
   * This fixes the mismatch between Skio API first variant and HTML first checked swatch
   *
   * @param {HTMLElement} productCard - The product card DOM element
   * @param {Object} skioProduct - Product object from Skio API with allowed variants
   */
  function syncCardDataWithCheckedSwatch(productCard, skioProduct) {
    // console.log('=== SYNCING CARD DATA WITH CHECKED SWATCH ===');

    // Find the checked swatch input across all containers
    const checkedSwatch = productCard.querySelector('.js-color-swatches input[type="radio"]:checked')

    if (!checkedSwatch) {
      console.warn('No checked swatch found for:', skioProduct.productTitle)
      return
    }

    const checkedVariantId = checkedSwatch.dataset.variantId
    // console.log('Checked swatch variant ID:', checkedVariantId);

    // Find this variant in the Skio product data
    const skioVariant = skioProduct.productVariants.find((v) => extractVariantId(v.platformId) === checkedVariantId)

    if (!skioVariant) {
      console.warn('Checked variant not found in Skio data:', checkedVariantId)
      return
    }

    // console.log('Found matching Skio variant:', skioVariant.productVariantTitle);

    // Resolve the correct market-aware price for this variant (subscription-
    // adjusted when a plan is active). Falls back to Skio base cents if the
    // market fetch didn't populate this variant.
    const { price, compareAtPrice, priceFormatted } = resolveVariantPrice(skioVariant)

    // Update card data attributes to match the checked swatch
    productCard.setAttribute('data-variant-id', checkedVariantId)
    productCard.setAttribute('data-variant-title', skioVariant.productVariantTitle)
    productCard.setAttribute('data-price', price)
    productCard.setAttribute('data-compare-at-price', compareAtPrice)

    // Update variant image
    if (skioVariant.imageSrc) {
      productCard.setAttribute('data-variant-image', skioVariant.imageSrc)
      // console.log('Set variant image:', skioVariant.imageSrc);
    } else {
      console.warn('No image for variant:', checkedVariantId)
    }

    // Update the price next to title
    const priceElement = productCard.querySelector('.card__title.price')
    if (priceElement) {
      priceElement.textContent = priceFormatted || formatPrice(price)
    }

    // Sync quantity selector with cart state and update UI
    syncQuantitySelectorWithState(checkedVariantId, productCard)

    // console.log('Card data synced with checked swatch:', checkedVariantId);
    // console.log('=== END SYNC ===\n');

    // Re-initialize mobile swatch features after filtering/syncing
    // This ensures scroll arrows and centering work correctly with remaining swatches
    if (typeof window.initMobileSwatchScrolling === 'function') {
      setTimeout(() => {
        window.initMobileSwatchScrolling()
      }, 100)
    }

    if (typeof window.centerMobileSwatches === 'function') {
      setTimeout(() => {
        window.centerMobileSwatches()
      }, 150)
    }
  }

  /**
   * Extract numeric variant ID from Shopify GID
   *
   * @param {String} platformId - Shopify GID (e.g., "gid://shopify/ProductVariant/39902657282097")
   * @returns {String} Numeric variant ID (e.g., "39902657282097")
   */
  function extractVariantId(platformId) {
    if (!platformId) return ''

    // Handle both GID format and plain numeric IDs
    if (platformId.includes('gid://shopify/ProductVariant/')) {
      return platformId.replace('gid://shopify/ProductVariant/', '')
    }

    return platformId.toString()
  }

  /**
   * Fetch Shopify's market-aware prices for every variant of each product in
   * the bundle. For each product handle we hit the Section Rendering API at
   *   /products/{handle}?section_id=bundle-variant-prices
   * which returns a small JSON blob produced by Liquid's `money` filter. That
   * filter is market-aware and respects *manual* per-market prices set in the
   * admin (things like a clean "€19.90" that `Shopify.currency.rate` can't
   * reproduce from a USD base). Results are stored in
   * BundleBuilderState.marketPrices keyed by variant id.
   *
   * Failures are swallowed per-product — callers fall back to Skio's base
   * prices when a variant is missing from the map.
   *
   * @param {Array<Object>} skioProducts - The filtered Skio product list.
   */
  async function fetchMarketPricesForProducts(skioProducts) {
    const uniqueHandles = Array.from(new Set(skioProducts.map((p) => p.productHandle).filter(Boolean)))
    const MARKET_PRICE_FETCH_TIMEOUT_MS = 8000

    await Promise.all(
      uniqueHandles.map(async (handle) => {
        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), MARKET_PRICE_FETCH_TIMEOUT_MS)
        try {
          const response = await fetch(`/products/${handle}?section_id=bundle-variant-prices`, {
            signal: controller.signal,
          })
          if (!response.ok) return

          const html = await response.text()
          const doc = new DOMParser().parseFromString(html, 'text/html')
          const script = doc.querySelector('script[data-bundle-variant-prices]')
          if (!script) return

          const variants = JSON.parse(script.textContent || '[]')
          variants.forEach((v) => {
            const allocations = {}
            if (Array.isArray(v.selling_plan_allocations)) {
              v.selling_plan_allocations.forEach((allocation) => {
                allocations[String(allocation.selling_plan_id)] = {
                  price: Number(allocation.price) || 0,
                  priceFormatted: allocation.price_formatted || '',
                  compareAtPrice: Number(allocation.compare_at_price) || 0,
                  compareAtFormatted: allocation.compare_at_price_formatted || '',
                }
              })
            }

            BundleBuilderState.marketPrices.set(String(v.id), {
              price: Number(v.price) || 0,
              priceFormatted: v.price_formatted || '',
              compareAtPrice: Number(v.compare_at_price) || 0,
              compareAtFormatted: v.compare_at_price_formatted || '',
              allocations,
            })
          })
        } catch (e) {
          console.warn('Bundle Builder: failed to fetch market prices for', handle, e)
        } finally {
          clearTimeout(timeoutId)
        }
      })
    )

    return uniqueHandles
  }

  /**
   * Look up the authoritative market-aware price for a variant, optionally
   * for a specific selling plan. Returns a normalized
   * `{ price, priceFormatted, compareAtPrice, compareAtFormatted }` in
   * market-currency cents — or null if we don't have data yet (caller should
   * fall back to Skio base prices in that case).
   *
   * @param {String|Number} variantId
   * @param {String|Number|null} [sellingPlanId]
   * @returns {Object|null}
   */
  function getMarketPriceData(variantId, sellingPlanId) {
    if (variantId == null) return null
    const entry = BundleBuilderState.marketPrices.get(String(variantId))
    if (!entry) return null

    if (sellingPlanId != null) {
      const allocation = entry.allocations[String(sellingPlanId)]
      if (allocation) {
        // When a plan is active, the *original* variant price becomes the
        // shown compare-at, unless the allocation already carries one.
        return {
          price: allocation.price,
          priceFormatted: allocation.priceFormatted,
          compareAtPrice: allocation.compareAtPrice || entry.price,
          compareAtFormatted: allocation.compareAtFormatted || entry.priceFormatted,
        }
      }
    }

    return {
      price: entry.price,
      priceFormatted: entry.priceFormatted,
      compareAtPrice: entry.compareAtPrice,
      compareAtFormatted: entry.compareAtFormatted,
    }
  }

  /**
   * Resolve the display price + compare-at for a variant, preferring Shopify's
   * market-aware values and falling back to Skio's base-currency data. Used
   * by `renderProductCard`, `syncCardDataWithCheckedSwatch`, and
   * `handleCartUpdate` so all three paths agree on what the shopper sees.
   *
   * Returns market-currency cents (so they can be formatted by `formatPrice`
   * without any further rate conversion) plus Shopify's pre-formatted string
   * when available — the latter exactly matches the numbers rendered elsewhere
   * on the site, which is what users compare against.
   *
   * @param {Object} skioVariant - A variant from the Skio API response.
   * @returns {{
   *   price: number,
   *   compareAtPrice: number,
   *   priceFormatted: string,
   *   compareAtFormatted: string
   * }}
   */
  function resolveVariantPrice(skioVariant) {
    if (!skioVariant) {
      return { price: 0, compareAtPrice: 0, priceFormatted: '', compareAtFormatted: '' }
    }

    const variantId = extractVariantId(skioVariant.platformId)
    const market = getMarketPriceData(variantId, BundleBuilderState.currentSellingPlanId)

    if (market) {
      return {
        price: market.price,
        compareAtPrice: market.compareAtPrice || 0,
        priceFormatted: market.priceFormatted,
        compareAtFormatted: market.compareAtFormatted,
      }
    }

    // Fallback: Skio gave us a base-currency amount. Use the plan-adjusted
    // price when a subscription is active so the button still reflects the
    // discount even if the market-price fetch failed. Map keys are usually
    // GID strings; `platformId` may be numeric, so normalize for lookup.
    const idStr = extractVariantId(skioVariant.platformId)
    const gidKey = `gid://shopify/ProductVariant/${idStr}`
    const m = BundleBuilderState.currentPlanVariantMapping
    const planMapping = m && (m[gidKey] || m[String(skioVariant.platformId)] || m[idStr])

    const basePrice = Math.round((skioVariant.price || 0) * 100)
    const baseCompareAt = skioVariant.compareAtPrice ? Math.round(skioVariant.compareAtPrice * 100) : 0

    if (planMapping) {
      return {
        price: Math.round(planMapping.adjustedPrice * 100),
        compareAtPrice: Math.max(baseCompareAt, basePrice),
        priceFormatted: '',
        compareAtFormatted: '',
      }
    }

    return {
      price: basePrice,
      compareAtPrice: baseCompareAt,
      priceFormatted: '',
      compareAtFormatted: '',
    }
  }

  /**
   * Get Skio variant data directly from API response (source of truth)
   * This ensures we always have the correct imageSrc, price, and compareAtPrice
   *
   * @param {String} variantId - Numeric variant ID (e.g., "39902657282097")
   * @param {String} productHandle - Product handle (e.g., "lip-liner-stay-n")
   * @returns {Object|null} Skio variant object with imageSrc, price, compareAtPrice, etc.
   */
  function getSkioVariantData(variantId, productHandle) {
    const skioProduct = BundleBuilderState.skioProductsByHandle.get(productHandle)

    if (!skioProduct) {
      console.warn('getSkioVariantData: Product not found in lookup map:', productHandle)
      return null
    }

    const skioVariant = skioProduct.productVariants.find((v) => extractVariantId(v.platformId) === variantId)

    if (!skioVariant) {
      console.warn('getSkioVariantData: Variant not found in product:', variantId)
      return null
    }

    return skioVariant
  }

  /**
   * @param {String} variantId
   * @returns {String|null} product handle for this variant, if the product is in the bundle list
   */
  function getProductHandleForVariantId(variantId) {
    const id = String(variantId)
    for (const p of BundleBuilderState.skioProductsByHandle.values()) {
      if (p.productVariants.some((v) => extractVariantId(v.platformId) === id)) {
        return p.productHandle
      }
    }
    return null
  }

  /**
   * ============================================================================
   * CART MANAGEMENT
   * ============================================================================
   */

  /**
   * Initialize cart event listeners
   */
  function initCartEventListeners() {
    // console.log('Bundle Builder: Initializing cart event listeners...');

    // Listen for bundle:cartUpdate events from product cards
    document.addEventListener('bundle:cartUpdate', function (event) {
      const { variantId, quantity, productHandle } = event.detail
      handleCartUpdate(variantId, quantity, productHandle)
    })
  }

  /**
   * Handle cart update from product card
   *
   * NEW SIMPLIFIED APPROACH:
   * Look up variant data directly from Skio API response (source of truth).
   * This ensures we always have the correct imageSrc, price, and compareAtPrice.
   *
   * @param {String} variantId - Variant ID
   * @param {Number} quantity - New quantity
   * @param {String} productHandle - Product handle (e.g., "lip-liner-stay-n")
   */
  function handleCartUpdate(variantId, quantity, productHandle) {
    // console.log('Bundle Builder: Cart update -', { variantId, quantity, productHandle });

    // Look up variant data from Skio API (source of truth)
    const skioVariant = getSkioVariantData(variantId, productHandle)

    if (!skioVariant) {
      console.error('Bundle Builder: Cannot find variant in Skio data:', variantId)
      return
    }

    // Get product title from Skio data
    const skioProduct = BundleBuilderState.skioProductsByHandle.get(productHandle)
    const productTitle = skioProduct ? skioProduct.productTitle : ''

    // Normalize variant title: treat "Default Title" as empty (for products without variants)
    let variantTitle = skioVariant.productVariantTitle || ''
    if (variantTitle === 'Default Title') {
      variantTitle = ''
    }

    // Get image from Skio API - fall back to product image if variant image is empty
    let image = skioVariant.imageSrc || ''
    if (!image && skioProduct && skioProduct.productImageSrc) {
      image = skioProduct.productImageSrc
      // console.log('Bundle Builder: Using product-level image (variant image was empty):', image);
    }

    // Resolve price via the market-aware helper so the cart stores the same
    // cents the shopper saw on the card. Subscription adjustment is applied
    // there too — no need to re-do it here.
    const resolved = resolveVariantPrice(skioVariant)
    const finalPrice = resolved.price
    const finalCompareAtPrice = resolved.compareAtPrice

    // console.log('Bundle Builder: Using Skio data -', {
    //   productTitle,
    //   variantTitle,
    //   price: finalPrice / 100,
    //   compareAtPrice: finalCompareAtPrice / 100,
    //   image: image ? '✓ Image available' : '⚠️ No image'
    // });

    if (quantity > 0) {
      // Add or update item in cart
      BundleBuilderState.selectedProducts.set(variantId, {
        quantity,
        productTitle,
        variantTitle,
        price: finalPrice,
        compareAtPrice: finalCompareAtPrice,
        image,
      })
    } else {
      // Remove item from cart
      BundleBuilderState.selectedProducts.delete(variantId)
    }

    // Update UI
    updateCartUI()
    updateSliderFill()
  }

  /**
   * After `fetchMarketPricesForProducts` fills `marketPrices`, refresh bundle
   * card price UI and reprice cart lines so the DOM and MY BOX match the map.
   *
   * @param {String[]} uniqueHandles - Product handles whose market data was requested
   */
  function reconcileMarketPricesUI(uniqueHandles) {
    const container = document.getElementById('bundle-products')
    if (container) {
      for (const handle of uniqueHandles) {
        const skioProduct = BundleBuilderState.skioProductsByHandle.get(handle)
        if (!skioProduct) continue

        const card = Array.from(container.querySelectorAll('.bundle-card')).find(
          (el) => el.dataset.productHandle === handle
        )
        if (!card) continue

        const variantId = card.dataset.variantId
        if (!variantId) continue

        const skioVariant = getSkioVariantData(variantId, handle)
        if (!skioVariant) continue

        const resolved = resolveVariantPrice(skioVariant)
        const { price, compareAtPrice, priceFormatted } = resolved

        card.dataset.price = price
        card.dataset.compareAtPrice = compareAtPrice

        const priceElement = card.querySelector('.card__title.price')
        if (priceElement) {
          priceElement.textContent = priceFormatted || formatPrice(price)
        }

        const cartItem = BundleBuilderState.selectedProducts.get(variantId)
        const qty = cartItem ? cartItem.quantity : 0
        updateBundleCardActions(card, qty, price, variantId, compareAtPrice, priceFormatted)
      }
    }

    BundleBuilderState.selectedProducts.forEach((item, variantId) => {
      const productHandle = getProductHandleForVariantId(variantId)
      if (productHandle) {
        handleCartUpdate(variantId, item.quantity, productHandle)
      }
    })
  }

  /**
   * Run after the grid and session restore so cards and `selectedProducts` exist
   * when `reconcileMarketPricesUI` runs. Safe to call again after
   * `renderProducts` (e.g. subscription change) reassigns
   * `lastMarketPriceFetch` / `lastMarketPriceHandles`.
   */
  function flushMarketPriceReconciliation() {
    const fetchP = lastMarketPriceFetch
    const handles = lastMarketPriceHandles
    if (!fetchP || !handles || !handles.length) return
    fetchP.then((result) => {
      if (result === null) return
      reconcileMarketPricesUI(handles)
    })
  }

  /**
   * Update cart UI (both desktop and mobile)
   */
  function updateCartUI() {
    // console.log('Bundle Builder: Updating cart UI...');

    updateDesktopCart()
    updateMobileCart()
    saveToSession() // Persist state to session
    updateCartBadges()
    updateSubtotals()
  }

  /**
   * Sync a product card's quantity selector with current cart state
   * Called when user switches variants on a product card
   * @param {String} variantId - The currently selected variant ID
   * @param {HTMLElement} card - The product card element
   */
  function syncQuantitySelectorWithState(variantId, card) {
    // Check if this variant is in the cart
    const cartItem = BundleBuilderState.selectedProducts.get(variantId)
    const currentQuantity = cartItem ? cartItem.quantity : 0

    // Update UI to show ADD button or quantity selector based on quantity
    const price = card.dataset.price
    const compareAtPrice = card.dataset.compareAtPrice || price
    updateBundleCardActions(card, currentQuantity, price, variantId, compareAtPrice)
  }

  /**
   * Update desktop cart items
   */
  function updateDesktopCart() {
    const container = document.getElementById('bundle-cart-items-desktop')
    if (!container) return

    if (BundleBuilderState.selectedProducts.size === 0) {
      container.innerHTML = '<div class="bundle-builder__cart-empty"><p>Your bundle is empty</p></div>'
      return
    }

    container.innerHTML = ''

    BundleBuilderState.selectedProducts.forEach((item, variantId) => {
      const itemEl = createCartItemElement(variantId, item, 'desktop')
      container.appendChild(itemEl)
    })
  }

  /**
   * Update mobile cart items
   */
  function updateMobileCart() {
    const container = document.getElementById('bundle-cart-items-mobile')
    if (!container) return

    if (BundleBuilderState.selectedProducts.size === 0) {
      container.innerHTML = '<div class="bundle-builder__cart-empty"><p>Your bundle is empty</p></div>'
      return
    }

    container.innerHTML = ''

    BundleBuilderState.selectedProducts.forEach((item, variantId) => {
      const itemEl = createCartItemElement(variantId, item, 'mobile')
      container.appendChild(itemEl)
    })
  }

  /**
   * Create a cart item element
   * @param {String} variantId - Variant ID
   * @param {Object} item - Item data
   * @param {String} context - 'desktop' or 'mobile'
   * @returns {HTMLElement} Cart item element
   */
  function createCartItemElement(variantId, item, context) {
    const itemEl = document.createElement('div')
    itemEl.className = 'bundle-builder__cart-item'
    itemEl.dataset.variantId = variantId

    const imageWrapper = document.createElement('div')
    imageWrapper.className = 'bundle-builder__cart-item-image-wrapper'

    const img = document.createElement('img')
    img.src = item.image // Image is now guaranteed from product or variant (no placeholder)
    img.alt = item.productTitle
    img.className = 'bundle-builder__cart-item-image'
    imageWrapper.appendChild(img)

    // Quantity badge (top-left)
    const qtyBadge = document.createElement('div')
    qtyBadge.className = 'bundle-builder__cart-item-qty-badge focus-inset'
    qtyBadge.setAttribute('tabindex', '0')
    qtyBadge.textContent = item.quantity
    imageWrapper.appendChild(qtyBadge)

    // Remove button (top-right)
    const removeBtn = document.createElement('button')
    removeBtn.type = 'button'
    removeBtn.className = 'bundle-builder__cart-item-remove'
    removeBtn.innerHTML = '×'
    removeBtn.setAttribute('aria-label', 'Remove item')
    removeBtn.addEventListener('click', () => removeFromCart(variantId))
    imageWrapper.appendChild(removeBtn)

    const details = document.createElement('div')
    details.className = 'bundle-builder__cart-item-details'

    // Combine product title and variant title: "PRODUCT NAME - VARIANT NAME"
    // Don't show variant title if it's empty or "Default Title" (products without variants)
    const title = document.createElement('h4')
    title.className = 'bundle-builder__cart-item-title focus-inset'
    title.setAttribute('tabindex', '0')
    const combinedTitle =
      item.variantTitle && item.variantTitle !== 'Default Title'
        ? `${item.productTitle} - ${item.variantTitle}`
        : item.productTitle
    title.textContent = combinedTitle

    const prices = document.createElement('div')
    prices.className = 'bundle-builder__cart-item-prices focus-inset'
    prices.setAttribute('tabindex', '0')

    // Calculate tier discount based on current total items
    const totalItems = getTotalItemCount()
    const tierDiscount = getTierDiscount(totalItems)

    // Calculate display prices with tier discount applied
    let displayPrice = item.price // This already includes subscription discount if applicable
    let displayCompareAtPrice = item.compareAtPrice

    if (tierDiscount > 0) {
      // Apply tier discount to the current price
      const tierDiscountedPrice = Math.round(displayPrice * (1 - tierDiscount / 100))

      // Set compare-at to the pre-tier-discount price (but post-subscription if applicable)
      // This shows the price before tier discount was applied
      displayCompareAtPrice = displayPrice
      displayPrice = tierDiscountedPrice

      // console.log(`Tier discount (${tierDiscount}%): ${formatMoney(displayCompareAtPrice)} → ${formatMoney(displayPrice)}`);
    }

    // Show compare at price if:
    // 1. Tier discount is active (to show pre-tier price), OR
    // 2. There's a subscription compare-at price that's higher than current price
    if (
      (tierDiscount > 0 && displayCompareAtPrice > 0) ||
      (displayCompareAtPrice && displayCompareAtPrice > displayPrice)
    ) {
      const comparePrice = document.createElement('span')
      comparePrice.className = 'bundle-builder__cart-item-compare-price'
      comparePrice.textContent = formatMoney(displayCompareAtPrice)
      prices.appendChild(comparePrice)
    }

    // Show actual price (with tier discount applied if applicable)
    const price = document.createElement('span')
    price.className = 'bundle-builder__cart-item-price'
    price.textContent = formatMoney(displayPrice)
    prices.appendChild(price)

    details.appendChild(title)
    details.appendChild(prices)

    itemEl.appendChild(imageWrapper)
    itemEl.appendChild(details)

    return itemEl
  }

  /**
   * Remove item from cart
   * @param {String} variantId - Variant ID to remove
   */
  function removeFromCart(variantId) {
    // console.log('Bundle Builder: Removing item from cart:', variantId);

    const removedItem = BundleBuilderState.selectedProducts.get(variantId)
    if (removedItem) {
      // console.log('Removed item:', removedItem.productTitle, '-', removedItem.variantTitle, 'qty:', removedItem.quantity);
    }

    // Remove from state
    BundleBuilderState.selectedProducts.delete(variantId)

    // Find any product card with this variant selected and reset it to 0
    const matchingCard = document.querySelector(`.bundle-card[data-variant-id="${variantId}"]`)
    if (matchingCard) {
      const qtyInput = matchingCard.querySelector('.bundle-card__qty-input')
      if (qtyInput) {
        qtyInput.value = 0
        // console.log('Reset product card quantity to 0 for variant:', variantId);
      }
    }

    // Also try the legacy reset function if it exists (for backwards compatibility)
    if (window.resetBundleCard) {
      window.resetBundleCard(variantId)
    }

    // Update UI
    updateCartUI()
    updateSliderFill()
  }

  /**
   * Update cart count badges
   */
  function updateCartBadges() {
    const totalItems = getTotalItemCount()

    // Mobile badge
    const mobileBadge = document.getElementById('bundle-cart-badge-mobile')
    if (mobileBadge) {
      mobileBadge.textContent = `+${totalItems}`
    }
  }

  /**
   * Update subtotal and savings displays
   */
  function updateSubtotals() {
    const { subtotal, savings, originalTotal } = calculateSubtotalAndSavings()
    const formattedSubtotal = formatMoney(subtotal)
    const formattedSavings = formatMoney(savings)
    const formattedOriginal = formatMoney(originalTotal)
    const hasDiscount = savings > 0

    // Desktop subtotal
    const desktopSubtotal = document.getElementById('bundle-subtotal-desktop')
    if (desktopSubtotal) {
      desktopSubtotal.textContent = formattedSubtotal
    }

    // Mobile subtotal
    const mobileSubtotal = document.getElementById('bundle-subtotal-mobile')
    if (mobileSubtotal) {
      mobileSubtotal.textContent = formattedSubtotal
    }

    // Desktop original (strikethrough)
    const desktopOriginal = document.getElementById('bundle-subtotal-original-desktop')
    if (desktopOriginal) {
      if (hasDiscount) {
        desktopOriginal.textContent = formattedOriginal
        desktopOriginal.style.display = ''
      } else {
        desktopOriginal.style.display = 'none'
      }
    }

    // Mobile original (strikethrough)
    const mobileOriginal = document.getElementById('bundle-subtotal-original-mobile')
    if (mobileOriginal) {
      if (hasDiscount) {
        mobileOriginal.textContent = formattedOriginal
        mobileOriginal.style.display = ''
      } else {
        mobileOriginal.style.display = 'none'
      }
    }

    // Desktop savings
    const desktopSavings = document.getElementById('bundle-savings-desktop')
    const desktopSavingsText = document.getElementById('bundle-savings-text-desktop')
    if (desktopSavings && desktopSavingsText) {
      if (hasDiscount) {
        desktopSavingsText.textContent = `you're saving ${formattedSavings}!`
        desktopSavings.style.display = ''
      } else {
        desktopSavings.style.display = 'none'
      }
    }

    // Mobile savings
    const mobileSavings = document.getElementById('bundle-savings-mobile')
    const mobileSavingsText = document.getElementById('bundle-savings-text-mobile')
    if (mobileSavings && mobileSavingsText) {
      if (hasDiscount) {
        mobileSavingsText.textContent = `you're saving ${formattedSavings}!`
        mobileSavings.style.display = ''
      } else {
        mobileSavings.style.display = 'none'
      }
    }
  }

  /**
   * Calculate subtotal with discounts and savings
   * @returns {Object} { subtotal, savings, originalTotal } in cents
   */
  function calculateSubtotalAndSavings() {
    let baseTotal = 0

    // Sum up all item prices * quantities
    // Note: Prices already include subscription discount if applicable (applied in handleCartUpdate)
    BundleBuilderState.selectedProducts.forEach((item) => {
      baseTotal += item.price * item.quantity
    })

    // Apply quantity tier discount (bundle discount)
    // This is separate from subscription discount and applies on top
    const totalItems = getTotalItemCount()
    const tierDiscount = getTierDiscount(totalItems)
    let savings = 0
    let subtotal = baseTotal

    if (tierDiscount > 0) {
      // console.log(`Applying ${tierDiscount}% tier discount for ${totalItems} items`);
      savings = Math.round(baseTotal * (tierDiscount / 100))
      subtotal = baseTotal - savings
    }

    return {
      subtotal: Math.round(subtotal),
      savings: Math.round(savings),
      originalTotal: Math.round(baseTotal),
    }
  }

  /**
   * Calculate subtotal with discounts (legacy wrapper)
   * @returns {Number} Subtotal in cents
   */
  function calculateSubtotal() {
    return calculateSubtotalAndSavings().subtotal
  }

  /**
   * Get tier discount percentage for given item count
   * @param {Number} itemCount - Total item count
   * @returns {Number} Discount percentage (0 if no tier discount)
   */
  function getTierDiscount(itemCount) {
    let discount = 0

    // Find the highest applicable tier
    Object.entries(BundleBuilderState.percentDiscountTiers).forEach(([items, discountPercent]) => {
      const tierItems = parseInt(items)
      if (itemCount >= tierItems) {
        const tierDiscount = parseFloat(discountPercent)
        if (tierDiscount > discount) {
          discount = tierDiscount
        }
      }
    })

    return discount
  }

  /**
   * ============================================================================
   * MOBILE MY BOX TOGGLE
   * ============================================================================
   */

  /**
   * Initialize mobile cart toggle functionality
   */
  function initMobileCartToggle() {
    // console.log('Bundle Builder: Initializing mobile cart toggle...');

    const toggleBtn = document.getElementById('bundle-cart-expand')
    const toggleBar = document.getElementById('bundle-cart-toggle')
    const cartContent = document.getElementById('bundle-cart-content')
    const blurOverlay = document.getElementById('bundle-blur-overlay')
    const toggleIcon = document.getElementById('bundle-toggle-icon')
    const toggleText = document.getElementById('bundle-toggle-text')

    if (!toggleBtn || !toggleBar || !cartContent || !blurOverlay) return

    // SVG icon strings - using icon-btn-arrow.liquid with rotation
    const upArrowSVG =
      '<svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 18L33 18M33 18L20.1429 6M33 18L20.1429 30" stroke="currentColor" stroke-width="4" stroke-linecap="round" transform="rotate(-90 18 18)"/></svg>'
    const downArrowSVG =
      '<svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 18L33 18M33 18L20.1429 6M33 18L20.1429 30" stroke="currentColor" stroke-width="4" stroke-linecap="round" transform="rotate(90 18 18)"/></svg>'

    let isOpen = false

    // Function to open MY BOX
    function openCart() {
      if (isOpen) return // Already open, do nothing

      isOpen = true
      cartContent.classList.add('open')
      blurOverlay.classList.add('active')
      toggleIcon.innerHTML = downArrowSVG
      toggleText.textContent = 'Hide Items'
      // console.log('Bundle Builder: MY BOX opened');
    }

    // Function to close MY BOX
    function closeCart() {
      if (!isOpen) return // Already closed, do nothing

      isOpen = false
      cartContent.classList.remove('open')
      blurOverlay.classList.remove('active')
      toggleIcon.innerHTML = upArrowSVG
      toggleText.textContent = 'Show Items'
      // console.log('Bundle Builder: MY BOX closed');
    }

    // Click on toggle bar (anywhere) opens MY BOX when closed
    toggleBar.addEventListener('click', function () {
      // If clicking anywhere on the toggle bar and cart is closed, open it
      if (!isOpen) {
        openCart()
      }
      // If cart is open and clicking the button specifically, let the button handler close it
      // (button handler will be triggered after this due to event bubbling)
    })

    // Click on button (specific element) closes MY BOX when open
    toggleBtn.addEventListener('click', function (event) {
      // Only handle closing when already open
      if (isOpen) {
        // Stop propagation to prevent toggleBar handler from firing
        event.stopPropagation()
        closeCart()
      }
      // If closed, let the event bubble up to toggleBar handler to open the cart
    })

    // Close on blur overlay click
    blurOverlay.addEventListener('click', function () {
      if (isOpen) {
        closeCart()
      }
    })
  }

  /**
   * ============================================================================
   * CHECKOUT & CART INTEGRATION
   * ============================================================================
   */

  /**
   * Initialize checkout button handlers
   */
  function initCheckoutHandlers() {
    // console.log('Bundle Builder: Initializing checkout handlers...');

    const desktopBtn = document.getElementById('bundle-checkout-desktop')
    const mobileBtn = document.getElementById('bundle-checkout-mobile')

    if (desktopBtn) {
      desktopBtn.addEventListener('click', handleCheckout)
    }

    if (mobileBtn) {
      mobileBtn.addEventListener('click', handleCheckout)
    }
  }

  /**
   * Handle checkout button click
   */
  async function handleCheckout(event) {
    const btn = event.target

    // console.log('Bundle Builder: Checkout initiated...');

    // Validate minimum item count
    const totalItems = getTotalItemCount()
    const minItems = BundleBuilderState.minItems

    if (totalItems < minItems) {
      showToast(`Please add at least ${minItems} items to your bundle.`)
      return
    }

    // Disable button and show loading
    btn.disabled = true
    // const originalText = btn.textContent;
    // btn.textContent = 'Adding to cart...';

    try {
      // Add items to Shopify cart
      await addToShopifyCart()

      // Clear session storage before checkout
      clearSession()

      // Redirect to checkout
      window.location.href = '/checkout'
    } catch (error) {
      console.error('Bundle Builder: Checkout error:', error)
      showToast('There was an error adding your bundle to cart. Please refresh and try again')

      // Re-enable button
      btn.disabled = false
      // btn.textContent = originalText;
    }
  }

  /**
   * Add all selected items to Shopify cart
   * @returns {Promise} Resolves when items are added to cart
   */
  async function addToShopifyCart() {
    const items = []
    const dynamicBoxIndex = 0 // Increment if supporting multiple boxes

    BundleBuilderState.selectedProducts.forEach((item, variantId) => {
      const cartItem = {
        id: variantId,
        quantity: item.quantity,
        properties: {
          _dynamicBoxId: BundleBuilderState.boxId,
          _dynamicBoxIndex: dynamicBoxIndex,
        },
      }

      // Add selling plan if subscription selected
      if (BundleBuilderState.currentSellingPlanId) {
        cartItem.selling_plan = BundleBuilderState.currentSellingPlanId
      }

      items.push(cartItem)
    })

    // console.log('Bundle Builder: Adding items to cart:', items);

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ items }),
    })

    if (!response.ok) {
      throw new Error(`Shopify Cart API Error: ${response.status} ${response.statusText}`)
    }

    return await response.json()
  }

  /**
   * ============================================================================
   * HELPER FUNCTIONS
   * ============================================================================
   */

  /**
   * Toast notification system
   */
  let toastTimeout = null

  /**
   * Show toast notification
   * @param {String} message - Error message to display
   */
  function showToast(message) {
    const toast = document.getElementById('bundle-toast')
    const toastMessage = document.getElementById('bundle-toast-message')
    const toastClose = document.getElementById('bundle-toast-close')

    if (!toast || !toastMessage) return

    // Clear any existing timeout
    if (toastTimeout) {
      clearTimeout(toastTimeout)
      toastTimeout = null
    }

    // Set message
    toastMessage.textContent = message

    // Show toast
    toast.classList.add('show')

    // Auto-dismiss after 5 seconds
    toastTimeout = setTimeout(() => {
      hideToast()
    }, 5000)

    // Close button handler (remove any previous listeners first)
    if (toastClose) {
      const newCloseBtn = toastClose.cloneNode(true)
      toastClose.parentNode.replaceChild(newCloseBtn, toastClose)

      newCloseBtn.addEventListener('click', () => {
        hideToast()
      })
    }
  }

  /**
   * Hide toast notification
   */
  function hideToast() {
    const toast = document.getElementById('bundle-toast')

    if (toast) {
      toast.classList.remove('show')
    }

    // Clear timeout
    if (toastTimeout) {
      clearTimeout(toastTimeout)
      toastTimeout = null
    }
  }

  /**
   * Get total item count across all selected products
   * @returns {Number} Total item count
   */
  function getTotalItemCount() {
    let total = 0
    BundleBuilderState.selectedProducts.forEach((item) => {
      total += item.quantity
    })
    return total
  }

  /**
   * Format money amount. Delegates to `formatPrice` so subtotal, savings,
   * and compare-at prices all render in the shopper's currently selected
   * market currency. Inputs are market-currency cents already (that's what
   * `resolveVariantPrice()` stores in cart state).
   *
   * @param {Number} cents - Amount in market-currency cents
   * @returns {String} Formatted money string in active market currency
   */
  function formatMoney(cents) {
    return formatPrice(cents)
  }

  /**
   * Show loading state
   */
  function showLoading() {
    const loading = document.getElementById('bundle-loading')
    const content = document.getElementById('bundle-content')

    if (loading) loading.style.display = 'flex'
    if (content) content.style.display = 'none'
  }

  /**
   * Hide loading state and show content
   */
  function hideLoading() {
    const loading = document.getElementById('bundle-loading')
    const content = document.getElementById('bundle-content')

    if (loading) loading.style.display = 'none'
    if (content) content.style.display = 'block'
  }

  /**
   * Show error message
   * @param {String} message - Error message to display
   */
  function showError(message) {
    const loading = document.getElementById('bundle-loading')
    if (loading) {
      loading.innerHTML = `<p style="color: rgb(var(--color-error, 200, 0, 0));">${message}</p>`
    }
  }

  /**
   * ============================================================================
   * GLOBAL EXPORTS (for external access if needed)
   * ============================================================================
   */

  /**
   * Get adjusted price for a variant based on current subscription plan
   * @param {String} variantId - Variant ID (numeric string)
   * @param {Number} basePrice - Base price in cents
   * @returns {Object} {price, compareAtPrice} in cents
   */
  function getAdjustedPrice(variantId, basePrice) {
    if (BundleBuilderState.currentPlanVariantMapping) {
      const idStr = extractVariantId(variantId)
      const variantGid = `gid://shopify/ProductVariant/${idStr}`
      const m = BundleBuilderState.currentPlanVariantMapping
      const variantMapping = m[variantGid] || m[idStr] || m[String(variantId)]

      if (variantMapping) {
        return {
          price: Math.round(variantMapping.adjustedPrice * 100),
          compareAtPrice: basePrice,
        }
      }
    }
    return {
      price: basePrice,
      compareAtPrice: 0,
    }
  }

  /**
   * Get the price to display on a bundle card for a given variant. Prefers
   * Shopify's market-aware price (including Liquid's pre-formatted money
   * string) and falls back to legacy Skio-base-cents + plan adjustment if
   * the market data isn't available yet.
   *
   * Consumed by `bundle-builder-product-card.js` when the shopper swaps
   * color/variant — see `updateCardVariantData` / `updateAddToBagButton`.
   *
   * @param {String|Number} variantId
   * @param {Number} fallbackBaseCents - Variant price in base cents (from
   *   /products/HANDLE.js), used only when market data is unavailable.
   * @param {Number} [fallbackCompareAtCents] - Compare-at in cents from
   *   product JSON when market data is unavailable; keeps strike pricing
   *   consistent with the authoritative payload.
   * @returns {{price:number, compareAtPrice:number, priceFormatted:string, compareAtFormatted:string}}
   */
  function getCardDisplayPrice(variantId, fallbackBaseCents, fallbackCompareAtCents) {
    const market = getMarketPriceData(variantId, BundleBuilderState.currentSellingPlanId)
    if (market) {
      return {
        price: market.price,
        compareAtPrice: market.compareAtPrice || 0,
        priceFormatted: market.priceFormatted || '',
        compareAtFormatted: market.compareAtFormatted || '',
      }
    }

    const base = fallbackBaseCents || 0
    const compareFallback = fallbackCompareAtCents || 0
    const maxStrike = Math.max(compareFallback, base)
    const adjusted = getAdjustedPrice(String(variantId), base)
    return {
      price: adjusted.price,
      compareAtPrice: Math.max(adjusted.compareAtPrice || 0, maxStrike),
      priceFormatted: '',
      compareAtFormatted: '',
    }
  }

  // Export state and functions for debugging and external use
  window.BundleBuilder = {
    state: BundleBuilderState,
    updateCartUI: updateCartUI,
    updateSliderFill: updateSliderFill,
    formatMoney: formatMoney,
    getAdjustedPrice: getAdjustedPrice,
    getCardDisplayPrice: getCardDisplayPrice,
    syncQuantitySelectorWithState: syncQuantitySelectorWithState,
    updateBundleCardActions: updateBundleCardActions,
  }
})()
